import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { filterMsOptions, orderedSelected, type MsOption } from './searchableMultiSelect.ts';
import { placePanel, type PanelPlacement } from '../lib/dropdownPlacement.ts';
import { scrollableBoundary } from './scrollableBoundary.ts';

/** Sentinel standing in for the "(Not set)" row inside `selected`. */
export const MULTISELECT_NOT_SET = '__none__';

/** Panel width in px (matches the former `w-72`); used for viewport clamping. */
const PANEL_WIDTH = 288;

/** Cap on the whole panel. Matches its natural full height today (search ≈50
 *  + header ≈30 + options list ≤240 + footer ≈46), so nothing changes when
 *  space is ample; when clamped tighter, the options list flexes and scrolls
 *  while the search/header/footer chrome stays visible. */
const PANEL_MAX_HEIGHT = 384;

// Matches the closed-control styling of the other filter triggers.
const triggerCls =
  'flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:border-slate-300 focus:border-slate-400 focus:outline-none';

/**
 * Searchable multi-select filter dropdown. The panel has a search box, a
 * Select-all / Clear header, a checkbox list (label + optional subtitle), and a
 * footer with the staged count and an Apply button. Selections are STAGED in a
 * local draft: toggling edits the draft only; Apply commits via onChange and
 * closes; outside-click / Escape discard the draft. The trigger badge reflects
 * the APPLIED selection, not the draft.
 *
 * The panel is rendered in a portal with fixed positioning so it overlays
 * everything rather than being clipped by — or shifting — an ancestor with
 * `overflow-hidden`/`overflow-x-auto` (e.g. the filter row that sits inside a
 * table card). Its position is anchored to the trigger and re-computed on
 * scroll/resize while open.
 */
export function SearchableMultiSelect({
  label,
  values,
  selected,
  onChange,
  includeNotSet = false,
  unitLabel = 'options',
  labelFor,
  subtitleFor,
  ariaLabel,
  className = '',
}: {
  label: string;
  values: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  includeNotSet?: boolean;
  unitLabel?: string;
  labelFor?: (value: string) => string;
  subtitleFor?: (value: string) => string | undefined;
  /** Overrides the trigger's accessible name. Defaults to `Filter by ${label}`
   *  (right for filters); assignment editors pass e.g. "Assign stations". */
  ariaLabel?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string[]>(selected);
  const [pos, setPos] = useState<PanelPlacement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  const options: MsOption[] = useMemo(() => {
    const base: MsOption[] = values.map((v) => {
      const subtitle = subtitleFor?.(v);
      return {
        value: v,
        label: labelFor ? labelFor(v) : v,
        ...(subtitle !== undefined ? { subtitle } : {}),
      };
    });
    if (includeNotSet) base.push({ value: MULTISELECT_NOT_SET, label: '(Not set)' });
    return base;
  }, [values, includeNotSet, labelFor, subtitleFor]);

  const allValues = useMemo(() => options.map((o) => o.value), [options]);

  // Anchor the fixed-position panel to the trigger, clamped to the viewport
  // horizontally and flipped/height-capped vertically by placePanel — measured
  // against the nearest scrollable ancestor so a panel inside a modal stays
  // inside it.
  function reposition() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const b = scrollableBoundary(el);
    setPos(
      placePanel({
        trigger: { top: r.top, bottom: r.bottom, left: r.left, width: r.width },
        boundary: b ? { top: b.top, bottom: b.bottom } : null,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        panelWidth: PANEL_WIDTH,
        maxPanelHeight: PANEL_MAX_HEIGHT,
      }),
    );
  }

  function openPanel() {
    setDraft(selected); // seed the draft from the applied selection
    setQuery('');
    reposition();
    setOpen(true);
  }

  // While open: outside-click / Escape close and discard the draft; the panel
  // re-anchors on scroll/resize. The pointer check spans the trigger root AND
  // the portaled panel, since the panel is no longer a DOM child of the root.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => reposition();
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReposition);
    // Capture phase so scrolls inside any nested container also re-anchor.
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const draftSet = new Set(draft);
  const filtered = filterMsOptions(options, query);

  function toggle(value: string) {
    const next = new Set(draftSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setDraft(orderedSelected(allValues, next));
  }
  function selectAllFiltered() {
    const next = new Set(draftSet);
    for (const o of filtered) next.add(o.value);
    setDraft(orderedSelected(allValues, next));
  }
  function clearAll() {
    setDraft([]);
  }
  function apply() {
    onChange(draft);
    setOpen(false);
  }

  const count = selected.length;

  const panel =
    open && pos
      ? createPortal(
          <div
            ref={panelRef}
            role="listbox"
            aria-multiselectable="true"
            aria-labelledby={labelId}
            style={{
              position: 'fixed',
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
              ...(pos.top != null ? { top: pos.top } : {}),
              ...(pos.bottom != null ? { bottom: pos.bottom } : {}),
            }}
            className="z-50 flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
          >
            <span id={labelId} className="sr-only">
              {label}
            </span>

            {/* Search */}
            <div className="border-b border-slate-100 p-2">
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${unitLabel}…`}
                className="w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
              />
            </div>

            {/* Header: count + Select all / Clear */}
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5">
              <span className="text-xs text-slate-500">
                All {options.length} {unitLabel}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 text-xs font-medium text-[#4D148C] hover:bg-[#F3EBFA]"
                  onClick={selectAllFiltered}
                >
                  Select all
                </button>
                <span className="text-xs text-slate-300">·</span>
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  onClick={clearAll}
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Options */}
            <div className="max-h-60 min-h-0 flex-1 overflow-auto py-1">
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-400">No matches</p>
              ) : (
                filtered.map((opt) => {
                  const checked = draftSet.has(opt.value);
                  return (
                    <label
                      key={opt.value}
                      role="option"
                      aria-selected={checked}
                      className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                        checked={checked}
                        onChange={() => toggle(opt.value)}
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-slate-700">{opt.label}</span>
                        {opt.subtitle && (
                          <span className="block truncate text-xs text-slate-400">
                            {opt.subtitle}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            {/* Footer: staged count + Apply */}
            <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
              <span className="text-xs text-slate-500">{draft.length} selected</span>
              <button
                type="button"
                onClick={apply}
                className="rounded-md bg-[#4D148C] px-3 py-1.5 text-sm font-medium text-white transition hover:bg-[#3B0F6B]"
              >
                Apply
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={`relative sm:w-52 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerCls}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? `Filter by ${label}`}
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{label}</span>
          {count > 0 && (
            <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-slate-900 px-1.5 text-xs font-medium text-white">
              {count}
            </span>
          )}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {panel}
    </div>
  );
}
